import {
  OSU_PLAYFIELD_HEIGHT,
  OSU_PLAYFIELD_WIDTH,
  type CirclesChart,
  type KiaiSection,
} from '@shared/circlesChart';
import type { CirclesEngineState } from '@shared/engine/circles/state';
import type { PlayfieldRect } from '../circlesPlayfieldGeometry';
import type { CirclesParticle } from './circlesRenderTypes';
import {
  COMBO_HUE_CYCLE_THRESHOLD,
  KIAI_BURST_LIFE,
  KIAI_BURST_PARTICLE_COUNT,
  KIAI_BURST_SPEED,
  KIAI_HIT_SCALE_PEAK,
  KIAI_OVERLAY_ALPHA_PEAK,
  STAR_COUNT,
} from './circlesRenderConstants';
import { COMBO_PALETTE, hexToRgba, readableComboPalette } from './circlesRenderColour';
import type { RenderQuality } from '../../rendering/renderQuality';

interface Star {
  x: number;
  y: number;
  speed: number;
  size: number;
  alpha: number;
}

/**
 * Find the index of the kiai section containing `beat`, or -1 when
 * none does. Linear walk because kiai sections are typically 0-4
 * per chart — a sorted-then-bsearch implementation is overkill at
 * this scale and would obscure the simple `start <= b && b < end`
 * intent.
 */
export function activeKiaiIndex(
  beat: number,
  sections: ReadonlyArray<KiaiSection> | undefined,
): number {
  if (!sections || sections.length === 0) {
    return -1;
  }
  for (let i = 0; i < sections.length; i++) {
    const s = sections[i];
    if (beat >= s.startBeat && beat < s.endBeat) {
      return i;
    }
  }
  return -1;
}

/**
 * Per-beat envelope inside a kiai section: 1 on the downbeat,
 * decaying to 0 at the next beat (cubic ease-out). Drives the
 * brightness pulse and the hit-circle scale wobble so both pulse
 * in lockstep with the music.
 */
export function kiaiBeatEnvelope(beat: number): number {
  const frac = beat - Math.floor(beat);
  const t = 1 - frac;
  return t * t * t;
}

/**
 * Per-frame kiai scale boost applied to every hit-object radius. Folds
 * the reduced-motion gate so callers don't have to re-check it.
 */
export function kiaiScaleBoost(
  kiaiIdx: number,
  beat: number,
  reducedMotion: boolean,
): number {
  if (kiaiIdx < 0 || reducedMotion) {
    return 0;
  }
  return KIAI_HIT_SCALE_PEAK * kiaiBeatEnvelope(beat);
}

/**
 * Owns the parallax star layer and the once-per-section kiai burst
 * tracker. Both are render-only state with no engine-side meaning,
 * so they live on the scene subsystem rather than the orchestrator.
 */
export class SceneRenderer {
  private stars: Star[] = [];
  /**
   * Indices of {@link CirclesChart.kiaiSections} for which we've
   * already spawned the section-start star burst this loop. Walked
   * each frame in {@link maybeSpawnKiaiBurst} — once a section's
   * index lands here we don't burst again until the loop wraps
   * (cleared by {@link clearLoopBoundFx}), so a player tabbing back
   * mid-kiai mid-loop doesn't get a fresh burst.
   */
  private kiaiBurstsSpawned = new Set<number>();
  private reducedMotion: boolean;
  private quality: RenderQuality;
  private bgGradient: CanvasGradient | null = null;
  private bgGradientKey = '';
  private starCount: number;
  /**
   * Cached grid path — recomputed only when the playfield rect
   * changes. Walking 12 separate `beginPath`/`moveTo`/`lineTo`/
   * `stroke` calls every frame to redraw geometry that's identical
   * frame-to-frame is pure waste; `ctx.stroke(path)` against a
   * pre-built `Path2D` is one call that the browser can dispatch
   * straight to the rasteriser. NaN sentinels for the cached rect
   * dimensions so the first call always rebuilds (NaN !== NaN).
   */
  private gridPath: Path2D | null = null;
  private gridFieldX = Number.NaN;
  private gridFieldY = Number.NaN;
  private gridFieldW = Number.NaN;
  private gridFieldH = Number.NaN;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
    // Stars are pure decoration — the parallax barely registers
    // against the gradient + kiai bloom + hit FX. On low tier the
    // bg gradient alone reads as space, and skipping the per-star
    // fillStyle string allocation + fillRect on every frame trims
    // a measurable slice off the scene pass on busy frames.
    this.starCount = quality.decorations ? STAR_COUNT : 0;
    this.initStars();
  }

  private initStars(): void {
    this.stars = [];
    for (let i = 0; i < this.starCount; i++) {
      this.stars.push({
        x: Math.random() * OSU_PLAYFIELD_WIDTH,
        y: Math.random() * OSU_PLAYFIELD_HEIGHT,
        speed: 14 + Math.random() * 60,
        size: Math.random() < 0.7 ? 1 : 2,
        alpha: 0.18 + Math.random() * 0.5,
      });
    }
  }

  /** Drop the kiai burst tracker on loop wrap so each section fires once per loop. */
  clearLoopBoundFx(): void {
    this.kiaiBurstsSpawned.clear();
  }

  drawBackground(
    ctx: CanvasRenderingContext2D,
    chart: CirclesChart,
    state: CirclesEngineState,
    beat: number,
    w: number,
    h: number,
    beatPulse: number,
    kiaiIdx: number,
  ): void {
    // Cache the linear background gradient — its inputs (height +
    // palette) only change on resize or chart swap so allocating a
    // fresh `CanvasGradient` per frame is pure waste on mobile.
    const key = `${h}|${chart.palette.bg}|${chart.palette.bgAccent}`;
    if (!this.bgGradient || this.bgGradientKey !== key) {
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, chart.palette.bg);
      grad.addColorStop(1, chart.palette.bgAccent);
      this.bgGradient = grad;
      this.bgGradientKey = key;
    }
    ctx.fillStyle = this.bgGradient;
    ctx.fillRect(0, 0, w, h);

    // The bloom + kiai radials are full-viewport overlays. Both get
    // skipped on low tier — the background gradient + ambient hit FX
    // already convey "the music is hyped" without paying for two more
    // full-canvas radial gradient paints per frame.
    if (!this.quality.backgroundBloom) {
      return;
    }

    if (beatPulse > 0.05) {
      // Bloom hue: chart pad colour by default, but at high combos the
      // bloom cycles through the combo palette so sustained perfect
      // play is rewarded with a moving colour landscape. Cycle is
      // beat-driven (one step every 4 beats) so it locks to the music
      // rather than wall-clock time.
      //
      // Deliberately uses the built-in `COMBO_PALETTE` rather than the
      // mapper-defined `chart.comboColours` even when present — the
      // bloom relies on four well-separated, deliberately-distant hues
      // for its "moving landscape" feel; mapper palettes can have
      // duplicates or low-contrast neighbours that would visibly
      // stutter the cycle (`heroes` ships with two near-greys, for
      // example). Hit-circle combo-cycling reads the chart palette via
      // `comboColour(chart, …)` for per-circle identity; the bloom is
      // a global ambient effect with different aesthetic constraints.
      const hueCycling = !this.reducedMotion && state.combo >= COMBO_HUE_CYCLE_THRESHOLD;
      const bloomColour = hueCycling
        ? COMBO_PALETTE[Math.abs(Math.floor(beat / 4)) % COMBO_PALETTE.length]
        : chart.palette.pad;
      // Boost the alpha cap when cycling so the colour reads even
      // through the normal background gradient. Reduced-motion users
      // get the original tame value.
      const alphaCap = this.reducedMotion ? 0.12 : hueCycling ? 0.28 : 0.22;
      const radial = ctx.createRadialGradient(
        w / 2,
        h / 2,
        0,
        w / 2,
        h / 2,
        Math.max(w, h) * 0.75,
      );
      radial.addColorStop(0, hexToRgba(bloomColour, alphaCap * beatPulse));
      radial.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = radial;
      ctx.fillRect(0, 0, w, h);
    }

    // Kiai overlay: radial wash tinted by the chart's perfect-pad
    // hue, pulsing with the beat. Drawn last so it tints anything the
    // bg gradient + bloom rendered. We pick `palette.padPerfect` as
    // the base hue so the wash reads as "the playfield's own colour,
    // intensified" rather than a generic blanket — the same hue
    // already shows up on perfect-judgement bursts, so kiai feels
    // like the chart cranking its own palette louder. Per-chart
    // palettes vary (Cradles' is amber, Sky High's is cyan, Cradles'
    // perfect-pad green); we don't bias toward any specific
    // temperature.
    if (kiaiIdx >= 0 && !this.reducedMotion) {
      const env = kiaiBeatEnvelope(beat);
      const a = KIAI_OVERLAY_ALPHA_PEAK * env;
      if (a > 0.001) {
        const radial = ctx.createRadialGradient(
          w / 2,
          h / 2,
          0,
          w / 2,
          h / 2,
          Math.max(w, h) * 0.75,
        );
        radial.addColorStop(0, hexToRgba(chart.palette.padPerfect, a));
        radial.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = radial;
        ctx.fillRect(0, 0, w, h);
      }
    }
  }

  drawStars(ctx: CanvasRenderingContext2D, w: number, h: number, dt: number, scrollMul: number): void {
    if (this.stars.length === 0) {
      return;
    }
    for (const s of this.stars) {
      s.x -= s.speed * dt * scrollMul;
      if (s.x < 0) {
        s.x = w + Math.random() * 100;
        s.y = Math.random() * h;
      }
      ctx.fillStyle = `rgba(255, 255, 255, ${s.alpha})`;
      ctx.fillRect(s.x, s.y, s.size, s.size);
    }
  }

  /**
   * Subtle border around the osu playfield + a faint gradient inside,
   * so the player has a clear "inside the play area" visual landmark
   * even when no circles are on screen.
   */
  drawPlayfieldFrame(
    ctx: CanvasRenderingContext2D,
    field: PlayfieldRect,
    chart: CirclesChart,
    beatPulse: number,
  ): void {
    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
    ctx.fillRect(field.x, field.y, field.w, field.h);

    // Outer halo: a glowing rectangular stroke that breathes on every
    // beat so the playfield visibly throbs with the music. Drawn first
    // so the inner border + grid stay crisp on top. The shadowBlur is
    // the expensive part — gated on the chromeShadows tier so phones
    // skip the blurred stroke pass.
    if (beatPulse > 0.05 && !this.reducedMotion && this.quality.chromeShadows) {
      const haloOuter = 8 + beatPulse * 6;
      ctx.strokeStyle = hexToRgba(chart.palette.padPerfect, 0.18 * beatPulse);
      ctx.lineWidth = haloOuter;
      ctx.shadowColor = chart.palette.padPerfect;
      ctx.shadowBlur = 24 * beatPulse;
      ctx.strokeRect(field.x + 0.5, field.y + 0.5, field.w - 1, field.h - 1);
      ctx.shadowBlur = 0;
    }

    const ringAlpha = 0.18 + beatPulse * 0.18;
    ctx.strokeStyle = hexToRgba(chart.palette.pad, ringAlpha);
    ctx.lineWidth = 2;
    ctx.strokeRect(field.x + 0.5, field.y + 0.5, field.w - 1, field.h - 1);

    // Faint vertical/horizontal grid for spatial reference. Skipped
    // on low tier — the outer border + inner gradient fill already
    // frame the playfield, and 14 stroke ops/frame is a small but
    // pure waste on phones with no compositing budget to spare.
    if (this.quality.decorations) {
      ctx.strokeStyle = hexToRgba(chart.palette.pad, 0.05 + beatPulse * 0.05);
      ctx.lineWidth = 1;
      ctx.stroke(this.getGridPath(field));
    }
    ctx.restore();
  }

  /**
   * Lazily build a single {@link Path2D} containing every grid line
   * inside the current playfield rect. Cached and reused frame-to-
   * frame; rebuilt only when the rect's footprint changes (resize
   * or letterbox shift). Single `ctx.stroke(path)` then draws every
   * line in one rasteriser dispatch instead of 12 separate calls.
   *
   * Cache key is four primitive `===` comparisons rather than a
   * concatenated string — the steady-state hit path allocates
   * nothing.
   */
  private getGridPath(field: PlayfieldRect): Path2D {
    if (
      this.gridPath &&
      this.gridFieldX === field.x &&
      this.gridFieldY === field.y &&
      this.gridFieldW === field.w &&
      this.gridFieldH === field.h
    ) {
      return this.gridPath;
    }
    const path = new Path2D();
    const cols = 8;
    for (let i = 1; i < cols; i++) {
      const x = field.x + (field.w / cols) * i;
      path.moveTo(x, field.y);
      path.lineTo(x, field.y + field.h);
    }
    const rows = 6;
    for (let i = 1; i < rows; i++) {
      const y = field.y + (field.h / rows) * i;
      path.moveTo(field.x, y);
      path.lineTo(field.x + field.w, y);
    }
    this.gridPath = path;
    this.gridFieldX = field.x;
    this.gridFieldY = field.y;
    this.gridFieldW = field.w;
    this.gridFieldH = field.h;
    return path;
  }

  /**
   * Spawn the kiai section-start star burst the first frame the
   * playhead enters a section we haven't burst for yet. No-op when
   * `kiaiIdx === -1`, when the section was already burst, or under
   * reduced-motion. Particles radiate from the playfield centre with
   * randomised directions and the chart palette as colour source so
   * the burst feels mapper-defined rather than generic.
   *
   * Pushes into the particles list passed in by the caller (owned by
   * the particle subsystem) so this scene class doesn't need to know
   * about the pool's capacity rules.
   */
  maybeSpawnKiaiBurst(
    kiaiIdx: number,
    field: PlayfieldRect,
    chart: CirclesChart,
    particles: CirclesParticle[],
  ): void {
    if (kiaiIdx < 0 || this.reducedMotion) {
      return;
    }
    if (this.kiaiBurstsSpawned.has(kiaiIdx)) {
      return;
    }
    this.kiaiBurstsSpawned.add(kiaiIdx);
    const cx = field.x + (OSU_PLAYFIELD_WIDTH / 2) * field.scale;
    const cy = field.y + (OSU_PLAYFIELD_HEIGHT / 2) * field.scale;
    // Same per-chart readability-corrected palette the hit circles use,
    // so any dark mapper hue (e.g. a dark purple) brightens to visible
    // burst particles — already memoised, so no per-particle recompute.
    const palette = readableComboPalette(chart);
    for (let i = 0; i < KIAI_BURST_PARTICLE_COUNT; i++) {
      // Symmetric jitter (±0.2 rad around the evenly-spaced base
      // angle) — `Math.random() * 0.4` is asymmetric and biases the
      // whole burst CCW.
      const angle = (i / KIAI_BURST_PARTICLE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const speed = KIAI_BURST_SPEED * (0.6 + Math.random() * 0.6);
      const colour = palette[i % palette.length];
      const life = KIAI_BURST_LIFE;
      // Kiai bursts use the same default "square" particle as
      // mid-game hit bursts so the visual language stays consistent.
      particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life,
        maxLife: life,
        colour,
        size: 4 + Math.random() * 4,
        shape: 'square',
        rot: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 6,
      });
    }
  }
}
